// Extracted from WorkoutApp.jsx — active workout overlay
// V3: PB lookup across all sessions + header title ellipsis fix
import { useState, useEffect, useRef } from "react";
import { X, Plus, Check, Lightning } from "@phosphor-icons/react";
import { T } from "../../themes";
import { TODAY_ISO, fmtDuration } from "../../constants";
import {
  ExercisePicker, ExerciseBlock, RestTimer, PlateCalculator,
  epley1RM, toKg,
} from "../../workoutHelpers";
import AIText from "../AIText";

// Format session duration for the completion screen
// <60s: "7s" | 1-59m: "45 mins" | 60+m: "1h 5m"
function fmtSessionDuration(secs) {
  if (secs < 60) return secs + "s";
  const m = Math.floor(secs / 60);
  if (m < 60) return m + " min" + (m !== 1 ? "s" : "");
  const h = Math.floor(m / 60), rem = m % 60;
  return h + "h" + (rem > 0 ? " " + rem + "m" : "");
}

function estimateCaloriesBurnedLocal(exercises, durationSecs, weightKg) {
  if (!durationSecs || durationSecs < 60) return 0;
  if (!weightKg || weightKg < 20) weightKg = 75;
  const cats = new Set((exercises||[]).map(ex => ex.category || "Other"));
  const hasCardio = cats.has("Cardio");
  const hasStrength = ["Barbell","Dumbbell","Cable","Machine","Kettlebell","Band","Olympic"].some(c=>cats.has(c));
  const hasMobility = cats.has("Mobility") && cats.size === 1;
  let met;
  if (hasMobility) met = 3.0;
  else if (hasCardio && !hasStrength) met = 7.0;
  else if (hasCardio && hasStrength) met = 6.5;
  else met = 5.0;
  return Math.round(met * weightKg * (durationSecs / 3600));
}

export default function WorkoutScreen({library,unit,templateSession,sessions,onFinish,onCancel,profile,onRunAdvisor,effectiveIsPremium}){
  const BW_IDS=new Set(["e_pu","e_wpu","e_dpu","e_dip","e_pu2","e_cg","e_inv",
    "e_trdip","e_cbdip","e_npu","e_hs","e_crunch","e_sc","e_plk","e_spl","e_lrl","e_hlrl","e_abw"]);
  const userBW=parseFloat(profile?.weight)||80;
  const [name,setName]=useState(templateSession?.name||"");
  const [exercises,setExercises]=useState(()=>{
    if(!templateSession) return [];
    return (templateSession.exercises||[]).map(ex=>{
      const libEx=library.find(e=>e.id===ex.exerciseId);
      const prevSession=[...sessions].sort((a,b)=>b.date<a.date?-1:1).find(s=>(s.exercises||[]).some(e=>e.exerciseId===ex.exerciseId));
      const prevSets=prevSession?(prevSession.exercises||[]).find(e=>e.exerciseId===ex.exerciseId)?.sets||[]:[];
      const allHistorySets=sessions.flatMap(s=>(s.exercises||[]).filter(e=>e.exerciseId===ex.exerciseId).flatMap(e=>(e.sets||[]).filter(s=>s.done)));
      const pbSet=allHistorySets.length>0?allHistorySets.reduce((best,s)=>parseFloat(s.weight||0)>parseFloat(best.weight||0)?s:best,allHistorySets[0]):null;
      const pbBest=pbSet&&parseFloat(pbSet.weight)>0?{weight:parseFloat(pbSet.weight),reps:parseInt(pbSet.reps)||0}:null;
      return {
        exerciseId:ex.exerciseId, name:ex.name||libEx?.name||"Exercise",
        category:libEx?.category||"Other", muscles:libEx?.muscles||[],
        sets:(ex.sets||[{reps:"",weight:"",rpe:"",setType:"normal",done:false}]).map((s,i)=>({
          ...s, done:false, setType:s.setType||"normal",
          weight:"",
          reps:"",
        })),
        previousSets: prevSets.filter(s=>s.done),
        pbBest,
        superset: ex.superset||false,
      };
    });
  });
  const [showPicker,setShowPicker]=useState(false);
  const [elapsed,setElapsed]=useState(0);
  const [paused,setPaused]=useState(false);
  const [showPlates,setShowPlates]=useState(false);
  const startRef=useRef(Date.now());
  const pausedElapsedRef=useRef(0);
  const [showFinishSummary,setShowFinishSummary]=useState(null);
  const [reorderMode,setReorderMode]=useState(false);
  const dragRef=useRef({active:false,fromIdx:-1,startY:0});
  // Focus mode — show one exercise at a time
  const [currentExIdx,setCurrentExIdx]=useState(0);
  // Advisor state — scoped to the finish summary view
  const [advisorLoading,setAdvisorLoading]=useState(false);
  const [advisorResponse,setAdvisorResponse]=useState(null);
  // V40: styled discard confirmation
  const [showDiscardConfirm,setShowDiscardConfirm]=useState(false);
  // Auto-complete navigation modal (partial / all-empty sets)
  const [navModal,setNavModal]=useState(null);

  useEffect(()=>{
    if(exercises.length>0&&currentExIdx>=exercises.length) setCurrentExIdx(exercises.length-1);
    if(exercises.length===0&&currentExIdx!==0) setCurrentExIdx(0);
  },[exercises.length,currentExIdx]);
  useEffect(()=>{
    if(paused) return;
    // V28: Ensure startRef is valid (reset if somehow stale)
    if (!startRef.current || startRef.current > Date.now()) startRef.current = Date.now();
    const t=setInterval(()=>setElapsed(Math.floor((Date.now()-startRef.current)/1000)+pausedElapsedRef.current),1000);
    return()=>clearInterval(t);
  },[paused]); // eslint-disable-line react-hooks/exhaustive-deps
  function handlePause(){if(!paused){pausedElapsedRef.current=elapsed;setPaused(true);}else{startRef.current=Date.now();setPaused(false);}}
  function addExercise(ex){
    const prevSession=[...sessions].sort((a,b)=>b.date<a.date?-1:1).find(s=>(s.exercises||[]).some(e=>e.exerciseId===ex.id));
    const prevSets=prevSession?(prevSession.exercises||[]).find(e=>e.exerciseId===ex.id)?.sets||[]:[];
    const allHistorySets=sessions.flatMap(s=>(s.exercises||[]).filter(e=>e.exerciseId===ex.id).flatMap(e=>(e.sets||[]).filter(s=>s.done)));
    const pbSet=allHistorySets.length>0?allHistorySets.reduce((best,s)=>parseFloat(s.weight||0)>parseFloat(best.weight||0)?s:best,allHistorySets[0]):null;
    const pbBest=pbSet&&parseFloat(pbSet.weight)>0?{weight:parseFloat(pbSet.weight),reps:parseInt(pbSet.reps)||0}:null;
    const defWeight=BW_IDS.has(ex.id)?String(userBW):"";
    setExercises(prev=>[...prev,{
      exerciseId:ex.id, name:ex.name, category:ex.category, muscles:ex.muscles||[],
      sets:[{reps:"",weight:defWeight,rpe:"",setType:"normal",done:false},{reps:"",weight:defWeight,rpe:"",setType:"normal",done:false},{reps:"",weight:defWeight,rpe:"",setType:"normal",done:false}],
      previousSets:prevSets.filter(s=>s.done), pbBest, superset:false,
    }]);
    setShowPicker(false);
  }
  function addSet(exIdx){setExercises(prev=>prev.map((ex,i)=>i===exIdx?{...ex,sets:[...ex.sets,{reps:"",weight:ex.sets[ex.sets.length-1]?.weight||"",rpe:"",setType:"normal",done:false}]}:ex));}
  function removeSet(exIdx,setIdx){setExercises(prev=>prev.map((ex,i)=>i===exIdx?{...ex,sets:ex.sets.filter((_,j)=>j!==setIdx)}:ex));}
  function updateSet(exIdx,setIdx,field,val){setExercises(prev=>prev.map((ex,i)=>i===exIdx?{...ex,sets:ex.sets.map((s,j)=>j===setIdx?{...s,[field]:val}:s)}:ex));}
  function toggleDone(exIdx,setIdx){setExercises(prev=>prev.map((ex,i)=>i===exIdx?{...ex,sets:ex.sets.map((s,j)=>j===setIdx?{...s,done:!s.done}:s)}:ex));}
  function removeExercise(exIdx){setExercises(prev=>prev.filter((_,i)=>i!==exIdx));}
  function moveExerciseTo(fromIdx,toIdx){
    if(fromIdx===toIdx) return;
    setExercises(prev=>{
      const updated=[...prev];
      const [moved]=updated.splice(fromIdx,1);
      updated.splice(toIdx,0,moved);
      return updated;
    });
  }
  function handleNavigateForward(targetIdx){
    const ex=exercises[currentExIdx];
    const filledSets=ex.sets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
    const allEmpty=filledSets.length===0;
    const someEmpty=filledSets.length>0&&filledSets.length<ex.sets.length;
    if(allEmpty){
      setNavModal({type:"empty",onConfirm:()=>{setNavModal(null);setCurrentExIdx(targetIdx);}});
    }else if(someEmpty){
      setNavModal({type:"partial",onConfirm:()=>{
        setExercises(prev=>prev.map((e,i)=>i===currentExIdx?{...e,sets:e.sets.map(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0?{...s,done:true}:s)}:e));
        setNavModal(null);setCurrentExIdx(targetIdx);
      }});
    }else{
      setExercises(prev=>prev.map((e,i)=>i===currentExIdx?{...e,sets:e.sets.map(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0?{...s,done:true}:s)}:e));
      setCurrentExIdx(targetIdx);
    }
  }
  function substituteExercise(exIdx){setShowPicker({mode:"substitute",exIdx});}
  function handleSubPick(ex){
    const idx=showPicker.exIdx;
    const prevSession=[...sessions].sort((a,b)=>b.date<a.date?-1:1)
      .find(s=>(s.exercises||[]).some(e=>e.exerciseId===ex.id));
    const prevSets=prevSession?(prevSession.exercises||[]).find(e=>e.exerciseId===ex.id)?.sets||[]:[];
    const allHistorySets=sessions.flatMap(s=>(s.exercises||[]).filter(e=>e.exerciseId===ex.id).flatMap(e=>(e.sets||[]).filter(s=>s.done)));
    const pbSet=allHistorySets.length>0?allHistorySets.reduce((best,s)=>parseFloat(s.weight||0)>parseFloat(best.weight||0)?s:best,allHistorySets[0]):null;
    const pbBest=pbSet&&parseFloat(pbSet.weight)>0?{weight:parseFloat(pbSet.weight),reps:parseInt(pbSet.reps)||0}:null;
    setExercises(prev=>prev.map((e,i)=>i===idx?{
      exerciseId:ex.id, name:ex.name, category:ex.category, muscles:ex.muscles||[],
      sets:e.sets.map(s=>({weight:"",reps:"",rpe:"",done:false,setType:s.setType||"normal"})),
      previousSets:prevSets.filter(s=>s.done),
      pbBest,
      superset:e.superset||false,
    }:e));
    setShowPicker(false);
  }
  function finishWorkout(){
    // V26 HOTFIX: Count sets that are either marked done OR have weight+reps filled in
    const completedSetsTotal=exercises.reduce((a,ex)=>a+ex.sets.filter(s=>s.done||(s.weight&&parseFloat(s.weight)>0&&s.reps&&parseInt(s.reps)>0)).length,0);
    if(completedSetsTotal===0){if(!window.confirm("No sets completed. Discard?"))return;onCancel();return;}
    // Auto-mark sets with weight+reps as done for saving
    setExercises(prev=>prev.map(ex=>({...ex,sets:ex.sets.map(s=>
      (!s.done&&s.weight&&parseFloat(s.weight)>0&&s.reps&&parseInt(s.reps)>0)?{...s,done:true}:s
    )})));
    const finalExercises=exercises.map(ex=>{
      const libEx=library.find(l=>l.id===ex.exerciseId);
      const doneSets=ex.sets.filter(s=>s.done||(s.weight&&parseFloat(s.weight)>0&&s.reps&&parseInt(s.reps)>0)).map(s=>({...s,done:true,weight:toKg(parseFloat(s.weight)||0,unit)}));
      const prevBestW=(()=>{
        for(const sess of [...sessions].sort((a,b)=>b.date<a.date?-1:1)){
          const found=(sess.exercises||[]).find(e=>e.exerciseId===ex.exerciseId);
          if(found) return (found.sets||[]).filter(s=>s.done).reduce((b,s)=>Math.max(b,parseFloat(s.weight)||0),0);
        }
        return 0;
      })();
      const curBestW=doneSets.reduce((b,s)=>Math.max(b,parseFloat(s.weight)||0),0);
      const prev1RM=(()=>{for(const sess of [...sessions].sort((a,b)=>b.date<a.date?-1:1)){const f=(sess.exercises||[]).find(e=>e.exerciseId===ex.exerciseId);if(f)return(f.sets||[]).filter(s=>s.done).reduce((b,s)=>{const e1=epley1RM(parseFloat(s.weight)||0,parseInt(s.reps)||0);return e1>b?e1:b;},0);}return 0;})();
      const cur1RM=doneSets.reduce((b,s)=>{const e1=epley1RM(parseFloat(s.weight)||0,parseInt(s.reps)||0);return e1>b?e1:b;},0);
      const isPR=curBestW>prevBestW||cur1RM>prev1RM;
      return {exerciseId:ex.exerciseId,name:libEx?.name||ex.name,category:ex.category||libEx?.category||"Other",muscles:ex.muscles||libEx?.muscles||[],sets:doneSets,isPR,superset:ex.superset||false};
    }).filter(ex=>ex.sets.length>0);
    const safeDuration = Math.min(elapsed, 300 * 60); // V28: Cap at 300 mins (5 hours)
    const sess={name:name||"Workout",date:TODAY_ISO(),duration:safeDuration,exercises:finalExercises};
    setShowFinishSummary(sess);
  }

  if(showFinishSummary){
    const s=showFinishSummary;
    const prs=s.exercises.filter(e=>e.isPR);
    const totalVol=s.exercises.reduce((a,ex)=>a+ex.sets.reduce((b,st)=>b+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0);
    const totalSets=s.exercises.reduce((a,e)=>a+e.sets.filter(st=>st.done).length,0);
    const muscleGroups=[...new Set(s.exercises.flatMap(e=>(e.muscles||[]).slice(0,1)).filter(Boolean))];
    const calsBurned=estimateCaloriesBurnedLocal(s.exercises,s.duration,parseFloat(profile.weight)||75);
    const volDisplay=unit==="lbs"?Math.round(totalVol*2.20462).toLocaleString():Math.round(totalVol).toLocaleString();
    const volUnit=unit==="lbs"?"lbs":"kg";

    async function handleGetAdvisor(){
      setAdvisorLoading(true);
      try {
        const msg = await onRunAdvisor(s);
        setAdvisorResponse(msg || null);
      } catch(_) { setAdvisorResponse(null); }
      setAdvisorLoading(false);
    }

    return(
      <div style={{background:T.pageBg,width:"100%",padding:"24px 16px 40px"}}>
        {/* Header card */}
        <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary}`,padding:18,marginBottom:12}}>
          <p style={{color:T.primary,fontSize:28,fontWeight:800,margin:0,fontFamily:T.fontDisplay}}>Session Complete</p>
          <p style={{color:T.textDim,fontSize:12,margin:"4px 0 16px",fontFamily:T.fontSans,fontWeight:700}}>
            {s.name} · {s.exercises.length} exercise{s.exercises.length!==1?"s":""} · {fmtSessionDuration(s.duration)}
          </p>
          {/* Stats row */}
          <div style={{display:"flex",gap:8}}>
            <div style={{flex:1,background:T.surface,borderRadius:T.radiusSm,padding:"12px 10px"}}>
              <p style={{color:T.textDim,fontSize:9,fontWeight:900,letterSpacing:"0.8px",margin:0,fontFamily:T.fontSans,textTransform:"uppercase"}}>Volume</p>
              <p style={{color:T.ink,fontSize:24,fontWeight:800,margin:"6px 0 0",fontFamily:T.fontDisplay,lineHeight:1}}>{volDisplay}</p>
              <p style={{color:T.textDim,fontSize:11,fontWeight:700,margin:"2px 0 0",fontFamily:T.fontSans}}>{volUnit}</p>
            </div>
            <div style={{flex:1,background:T.surface,borderRadius:T.radiusSm,padding:"12px 10px"}}>
              <p style={{color:T.textDim,fontSize:9,fontWeight:900,letterSpacing:"0.8px",margin:0,fontFamily:T.fontSans,textTransform:"uppercase"}}>Sets</p>
              <p style={{color:T.ink,fontSize:24,fontWeight:800,margin:"6px 0 0",fontFamily:T.fontDisplay,lineHeight:1}}>{totalSets}</p>
              <p style={{color:T.textDim,fontSize:11,fontWeight:700,margin:"2px 0 0",fontFamily:T.fontSans}}>sets done</p>
            </div>
            <div style={{flex:1,background:T.surface,borderRadius:T.radiusSm,padding:"12px 10px"}}>
              <p style={{color:T.textDim,fontSize:9,fontWeight:900,letterSpacing:"0.8px",margin:0,fontFamily:T.fontSans,textTransform:"uppercase"}}>Calories</p>
              <p style={{color:T.ink,fontSize:24,fontWeight:800,margin:"6px 0 0",fontFamily:T.fontDisplay,lineHeight:1}}>{calsBurned > 0 ? calsBurned : "—"}</p>
              <p style={{color:T.textDim,fontSize:11,fontWeight:700,margin:"2px 0 0",fontFamily:T.fontSans}}>{calsBurned > 0 ? "kcal est." : "no data"}</p>
            </div>
          </div>
        </div>

        {/* PRs */}
        {prs.length>0&&(
          <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary}`,padding:18,marginBottom:12}}>
            <p style={{fontSize:11,fontWeight:900,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 10px",fontFamily:T.fontSans,textTransform:"uppercase"}}>Personal Records</p>
            {prs.map((e,i)=><p key={i} style={{margin:"4px 0",fontSize:13,fontFamily:T.fontSans,fontWeight:700,color:T.primary}}>PR: {e.name}</p>)}
          </div>
        )}

        {/* Exercises summary */}
        <div style={{background:T.card,borderRadius:T.radius,border:T.border,padding:18,marginBottom:12}}>
          <p style={{fontSize:11,fontWeight:900,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 12px",fontFamily:T.fontSans,textTransform:"uppercase"}}>Exercises</p>
          {s.exercises.map((ex,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",paddingTop:8,paddingBottom:8,borderBottom:i<s.exercises.length-1?T.border:"none"}}>
              <p style={{color:T.ink,fontSize:14,fontWeight:500,flex:1,margin:0,fontFamily:T.fontSans}}>{ex.isPR?"⭐ ":""}{ex.name}</p>
              <p style={{color:T.textDim,fontSize:13,margin:0,fontFamily:T.fontSans}}>{ex.sets.filter(st=>st.done).length} sets · {Math.round(ex.sets.filter(st=>st.done).reduce((sum,st)=>(parseFloat(st.weight)||0)*(parseInt(st.reps)||0)+sum,0))}{volUnit}</p>
            </div>
          ))}
        </div>

        {/* Muscle groups */}
        {muscleGroups.length>0&&(
          <div style={{background:T.card,borderRadius:T.radius,border:T.border,padding:18,marginBottom:16}}>
            <p style={{fontSize:11,fontWeight:900,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 12px",fontFamily:T.fontSans,textTransform:"uppercase"}}>Muscles Worked</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {muscleGroups.map((m,i)=>(
                <span key={i} style={{background:(T.primary)+'22',borderRadius:20,padding:"6px 12px",fontSize:12,fontWeight:700,color:T.primary,fontFamily:T.fontSans}}>{m}</span>
              ))}
            </div>
          </div>
        )}

        {/* Post-workout AI advisor — premium only */}
        {effectiveIsPremium && onRunAdvisor && (
          <div style={{marginBottom:16}}>
            {!advisorResponse && !advisorLoading && (
              <button
                onClick={handleGetAdvisor}
                style={{width:"100%",background:"transparent",border:`1.5px solid ${T.primary}`,borderRadius:T.radius,padding:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
              >
                <Lightning size={15} color={T.primary}/>
                <span style={{color:T.primary,fontSize:14,fontWeight:700,fontFamily:T.fontSans}}>Get post-workout analysis</span>
              </button>
            )}
            {advisorLoading && (
              <div style={{background:T.card,borderRadius:T.radius,border:T.border,padding:16,textAlign:"center"}}>
                <p style={{color:T.textDim,fontSize:13,fontFamily:T.fontSans,fontWeight:700,margin:0}}>Analysing your session…</p>
              </div>
            )}
            {advisorResponse && !advisorLoading && (
              <div style={{background:T.card,borderRadius:T.radius,border:T.border,padding:16}}>
                <p style={{color:T.primary,fontSize:10,fontWeight:900,letterSpacing:"0.8px",margin:"0 0 8px",fontFamily:T.fontSans,textTransform:"uppercase"}}>Post-Workout Analysis</p>
                <p style={{color:T.ink,fontSize:13,fontFamily:T.fontSans,fontWeight:700,margin:0,lineHeight:1.6}}>
                  <AIText text={advisorResponse}/>
                </p>
                <p style={{color:T.textDim,fontSize:10,fontFamily:T.fontSans,margin:"10px 0 0",lineHeight:1.4}}>
                  AI guidance only. Not medical advice. Consult a qualified professional for personalised nutrition plans.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Done button — always last */}
        <button onClick={()=>onFinish(showFinishSummary)} style={{width:"100%",background:T.primary,borderRadius:T.radius,padding:16,border:"none",cursor:"pointer"}}>
          <span style={{color:"#fff",fontSize:15,fontWeight:800,fontFamily:T.fontDisplay}}>Done</span>
        </button>
      </div>
    );
  }

  const doneSetsCount = exercises.reduce((a,ex)=>a+ex.sets.filter(s=>s.done).length,0);
  const totalSetsCount = exercises.reduce((a,ex)=>a+ex.sets.length,0);
  const progressPct = totalSetsCount > 0 ? Math.round((doneSetsCount / totalSetsCount) * 100) : 0;

  return(
    <div style={{background:T.pageBg,width:"100%"}}>
      {/* ── Top bar — matches Today screen header ────────────────────── */}
      <div style={{padding:"16px 18px 0",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4,background:T.pageBg,position:"sticky",top:0,zIndex:39}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0,overflow:"hidden"}}>
          <img src="/logo192.png" alt="LF" width={38} height={38} style={{flexShrink:0,borderRadius:11,objectFit:"cover"}}/>
          <div style={{minWidth:0,overflow:"hidden"}}>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Workout name..."
              style={{background:"transparent",border:"none",color:T.inkDark,fontSize:22,fontFamily:T.fontDisplay,fontWeight:800,outline:"none",width:"100%",padding:0,letterSpacing:"-0.5px",lineHeight:1,textOverflow:"ellipsis",overflow:"hidden",whiteSpace:"nowrap"}}/>
            <div style={{fontSize:11,fontWeight:500,color:T.inkMuted,fontFamily:T.fontDisplay,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>Active workout</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <div style={{background:T.surfaceLight,borderRadius:20,padding:"6px 12px",display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:"#f97316"}}/>
            <span style={{fontSize:13,fontWeight:800,color:T.inkDark,fontFamily:T.fontDisplay}}>{fmtDuration(elapsed)}</span>
          </div>
          <button onClick={handlePause} style={{background:T.surfaceLight,border:"none",borderRadius:20,padding:"6px 12px",fontSize:10,fontWeight:700,color:T.inkMuted,cursor:"pointer",fontFamily:T.fontSans}}>{paused?"Resume":"Pause"}</button>
          {/* App switcher pill — same as Today screen */}
          <div style={{display:'flex',background:T.card,borderRadius:20,padding:3,gap:2,border:`1px solid ${T.surfaceMid}`}}>
            <div style={{background:T.liftRed,borderRadius:20,padding:'6px 11px',display:'flex',alignItems:'center',gap:5}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill={T.tileText} stroke="none"><path d="M13 2L4.5 13.5H11L10 22l9.5-12H13.5L13 2z"/></svg>
              <span style={{fontSize:10,fontWeight:700,color:T.tileText,fontFamily:T.fontDisplay,letterSpacing:'0.3px'}}>Lift</span>
            </div>
            <div onClick={()=>{window.location.href='/';}} style={{borderRadius:20,padding:'6px 11px',display:'flex',alignItems:'center',gap:5,cursor:'pointer'}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22C8 22 5 18 5 14c0-5 4-12 7-12s7 7 7 12c0 4-3 8-7 8z"/><path d="M12 3c0 0-1 3-1 6"/></svg>
              <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontDisplay,letterSpacing:'0.3px'}}>Nutri</span>
            </div>
          </div>
          <button onClick={()=>setShowDiscardConfirm(true)} style={{width:28,height:28,borderRadius:"50%",background:T.surfaceLight,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <X size={14} color={T.inkMuted}/>
          </button>
        </div>
      </div>

      {/* ── Progress bar — matches Today card style ────────────────────── */}
      <div style={{background:T.card,padding:"8px 16px 10px",borderBottom:`1px solid ${T.cardBorder}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:11,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans}}>{doneSetsCount} of {totalSetsCount} sets done</span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={()=>setShowPlates(true)} style={{background:T.surfaceLight,border:"none",borderRadius:20,padding:"4px 10px",fontSize:9,fontWeight:600,color:T.tileMuted,cursor:"pointer",fontFamily:T.fontSans}}>Plates</button>
            {exercises.length>1&&<button onClick={()=>setReorderMode(r=>!r)} style={{background:T.surfaceLight,border:"none",borderRadius:20,padding:"4px 10px",fontSize:9,fontWeight:600,color:reorderMode?T.inkDark:T.tileMuted,cursor:"pointer",fontFamily:T.fontSans}}>{reorderMode?"Done":"Reorder"}</button>}
          </div>
          <span style={{fontSize:11,fontWeight:700,color:T.primary,fontFamily:T.fontDisplay}}>{progressPct}%</span>
        </div>
        <div style={{height:4,background:T.surfaceLight,borderRadius:2}}>
          <div style={{height:"100%",width:`${progressPct}%`,background:T.primary,borderRadius:2,transition:"width 0.3s ease"}}/>
        </div>
      </div>

      {/* ── Dot navigation ────────────────────────────────────────────── */}
      {!reorderMode&&exercises.length>0&&(
        <div style={{background:T.pageBg,padding:"10px 16px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",gap:5,alignItems:"center"}}>
            {exercises.map((_,i)=>{
              const isDone=exercises[i].sets.every(s=>s.done);
              const isCurrent=i===currentExIdx;
              return <button key={i} onClick={()=>setCurrentExIdx(i)} style={{width:isCurrent?10:8,height:isCurrent?10:8,borderRadius:isCurrent?5:4,
                background:isCurrent?(T.primary):isDone?(T.tileAccent):(T.cardBorder||"rgba(255,255,255,0.15)"),
                border:"none",padding:0,cursor:"pointer",transition:"all 0.2s"}}/>;
            })}
          </div>
          <span style={{fontSize:11,fontWeight:600,color:T.inkMuted||T.textDim,fontFamily:T.fontSans}}>Exercise {currentExIdx+1} of {exercises.length}</span>
        </div>
      )}

      <div style={{padding:"12px 16px 16px"}}>
        {reorderMode ? (
          <div style={{marginBottom:12}}>
            {exercises.map((ex,exIdx)=>{
              const ROW_H=56;
              const onPointerDown=(e)=>{
                dragRef.current={active:true,fromIdx:exIdx,startY:e.clientY};
                e.target.setPointerCapture(e.pointerId);
              };
              const onPointerUp=(e)=>{
                if(!dragRef.current.active) return;
                const delta=e.clientY-dragRef.current.startY;
                const steps=Math.round(delta/ROW_H);
                const toIdx=Math.max(0,Math.min(exercises.length-1,dragRef.current.fromIdx+steps));
                if(toIdx!==dragRef.current.fromIdx) moveExerciseTo(dragRef.current.fromIdx,toIdx);
                dragRef.current={active:false,fromIdx:-1,startY:0};
              };
              return (
                <div key={ex.exerciseId||exIdx} style={{display:"flex",alignItems:"center",background:T.card,
                  borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder||T.ink}`,padding:14,marginBottom:8,height:ROW_H,
                  boxSizing:"border-box",touchAction:"none"}}>
                  <span onPointerDown={onPointerDown} onPointerUp={onPointerUp}
                    style={{fontSize:20,color:T.textDim,paddingRight:14,cursor:"grab",userSelect:"none",touchAction:"none"}}>≡</span>
                  <span style={{flex:1,fontSize:15,fontWeight:600,color:T.text,fontFamily:T.fontSans}}>{ex.name}</span>
                  <span style={{fontSize:13,color:T.textDim,fontFamily:T.fontSans}}>#{exIdx+1}</span>
                </div>
              );
            })}
            <button onClick={()=>setReorderMode(false)} style={{width:"100%",marginTop:8,padding:14,
              background:T.primary,border:"none",borderRadius:T.radiusSm,cursor:"pointer",
              color:"#fff",fontSize:15,fontWeight:700,fontFamily:T.fontSans}}>Done Reordering</button>
          </div>
        ) : exercises.length>0 ? (<>
          <ExerciseBlock key={currentExIdx} ex={exercises[currentExIdx]} exIdx={currentExIdx} unit={unit} onAddSet={()=>addSet(currentExIdx)} onRemoveSet={(si)=>removeSet(currentExIdx,si)} onUpdateSet={(si,f,v)=>updateSet(currentExIdx,si,f,v)} onToggleDone={(si)=>toggleDone(currentExIdx,si)} onRemoveExercise={()=>removeExercise(currentExIdx)} previousSets={exercises[currentExIdx].previousSets||[]} allDone={exercises[currentExIdx].sets.every(s=>s.done)} onSubstitute={()=>substituteExercise(currentExIdx)} canSuperset={currentExIdx<exercises.length-1} isSuperset={exercises[currentExIdx].superset} onToggleSuperset={()=>setExercises(prev=>prev.map((e,i)=>i===currentExIdx?{...e,superset:!e.superset}:e))} libEx={library.find(l=>l.id===exercises[currentExIdx].exerciseId)} onDone={()=>{if(currentExIdx<exercises.length-1)handleNavigateForward(currentExIdx+1);}}/>
          {/* ── Up Next card ───────────────────────────────────────────────── */}
          {currentExIdx<exercises.length-1&&(()=>{
            const nextEx=exercises[currentExIdx+1];
            const nextLibEx=library.find(l=>l.id===nextEx.exerciseId);
            const nextDone=nextEx.sets.every(s=>s.done);
            return(
              <button onClick={()=>setCurrentExIdx(currentExIdx+1)} style={{width:"100%",background:T.card,border:`1px solid ${T.cardBorder||T.ink}`,borderRadius:T.radiusSm,padding:"12px 14px",marginTop:4,cursor:"pointer",display:"flex",alignItems:"center",gap:10,textAlign:"left"}}>
                <div style={{width:32,height:32,borderRadius:8,background:T.surfaceLight||T.surface,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {nextDone?<Check size={14} color={T.tileAccent}/>:<span style={{fontSize:12,fontWeight:800,color:T.inkMuted||T.textDim,fontFamily:T.fontDisplay}}>{currentExIdx+2}</span>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{fontSize:10,fontWeight:600,color:T.inkMuted||T.textDim,fontFamily:T.fontSans,display:"block"}}>Up next</span>
                  <span style={{fontSize:14,fontWeight:700,color:T.inkDark||T.text,fontFamily:T.fontDisplay,display:"block",marginTop:1}}>{nextEx.name||nextLibEx?.name||"Exercise"}</span>
                </div>
                <span style={{fontSize:11,fontWeight:600,color:T.inkMuted||T.textDim,fontFamily:T.fontSans,flexShrink:0}}>{nextEx.sets.length} sets →</span>
              </button>
            );
          })()}
        </>) : null}
        {!reorderMode&&(
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={()=>setShowPicker(true)} style={{flex:1,background:"none",border:`1.5px dashed ${T.cardBorder||T.ink}`,color:T.textSub,padding:"10px 0",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:700,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              <Plus size={14}/> Add exercise
            </button>
          </div>
        )}
        <div style={{marginTop:8}}><RestTimer/></div>
        {/* ── Bottom nav bar ──────────────────────────────────────────────── */}
        <div style={{position:"fixed",bottom:0,left:0,right:0,background:T.card,borderTop:`1px solid ${T.cardBorder||T.ink}`,padding:"12px 16px",zIndex:40}}>
          {reorderMode ? null : currentExIdx<exercises.length-1 ? (
            <div style={{display:"flex",gap:8}}>
              {currentExIdx>0&&(
                <button onClick={()=>setCurrentExIdx(i=>i-1)} style={{background:T.surfaceLight||T.surface,border:"none",borderRadius:T.radiusSm,padding:"14px 16px",fontSize:13,fontWeight:600,color:T.inkMuted||T.textDim,cursor:"pointer",fontFamily:T.fontSans}}>Back</button>
              )}
              <button onClick={()=>handleNavigateForward(currentExIdx+1)} style={{flex:1,background:T.orange||"#f97316",border:"none",borderRadius:T.radiusSm,padding:"14px",fontSize:14,fontWeight:800,color:"#fff",cursor:"pointer",fontFamily:T.fontDisplay,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                Next exercise →
              </button>
            </div>
          ) : (
            <div style={{display:"flex",gap:8}}>
              {exercises.length>1&&currentExIdx>0&&(
                <button onClick={()=>setCurrentExIdx(i=>i-1)} style={{background:T.surfaceLight||T.surface,border:"none",borderRadius:T.radiusSm,padding:"14px 16px",fontSize:13,fontWeight:600,color:T.inkMuted||T.textDim,cursor:"pointer",fontFamily:T.fontSans}}>← Back</button>
              )}
              <button onClick={finishWorkout} style={{flex:1,background:T.primary||T.accent,border:"none",color:"#fff",padding:"14px 0",cursor:"pointer",fontSize:14,fontFamily:T.fontDisplay,fontWeight:800,borderRadius:T.radiusSm,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <Check size={16}/> Finish workout
              </button>
            </div>
          )}
        </div>
      </div>
      {showPicker&&(typeof showPicker==="object"&&showPicker.mode==="substitute"
        ?<ExercisePicker library={library} onSelect={handleSubPick} onClose={()=>setShowPicker(false)}/>
        :<ExercisePicker library={library} onSelect={addExercise} onClose={()=>setShowPicker(false)}/>
      )}
      {showPlates&&<PlateCalculator unit={unit} onClose={()=>setShowPlates(false)}/>}
      {/* Navigation confirmation modal — partial / all-empty sets */}
      {navModal&&(
        <div style={{position:"fixed",inset:0,background:T.backdrop||"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div style={{background:T.card,borderRadius:T.radius,padding:24,width:"100%",maxWidth:320}}>
            <p style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark,margin:"0 0 6px",letterSpacing:"-0.3px"}}>
              {navModal.type==="empty"?"No sets recorded":"Some sets aren't filled in"}
            </p>
            <p style={{fontFamily:T.fontSans,fontSize:14,color:T.inkMuted,margin:"0 0 20px",lineHeight:1.4}}>
              {navModal.type==="empty"?"Skip this exercise and move on?":"Skip empty sets and move on?"}
            </p>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setNavModal(null)} style={{flex:1,background:T.surfaceLight,border:"none",borderRadius:T.radiusSm,padding:14,fontSize:14,fontWeight:700,color:T.inkDark,cursor:"pointer",fontFamily:T.fontSans}}>Go back</button>
              <button onClick={navModal.onConfirm} style={{flex:1,background:T.orange||"#f97316",border:"none",borderRadius:T.radiusSm,padding:14,fontSize:14,fontWeight:700,color:"#fff",cursor:"pointer",fontFamily:T.fontSans}}>
                {navModal.type==="empty"?"Skip exercise":"Skip empty sets"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* V40: Discard confirmation modal */}
      {showDiscardConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div style={{background:T.card,borderRadius:T.radius,padding:24,width:"100%",maxWidth:320}}>
            <p style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark,margin:"0 0 6px",letterSpacing:"-0.3px"}}>Discard workout?</p>
            <p style={{fontFamily:T.fontSans,fontSize:14,color:T.inkMuted,margin:"0 0 20px",lineHeight:1.4}}>All progress will be lost.</p>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowDiscardConfirm(false)} style={{flex:1,background:T.surfaceLight,border:"none",borderRadius:T.radiusSm,padding:14,fontSize:14,fontWeight:700,color:T.inkDark,cursor:"pointer",fontFamily:T.fontSans}}>Cancel</button>
              <button onClick={()=>{setShowDiscardConfirm(false);onCancel();}} style={{flex:1,background:T.danger||"#ef4444",border:"none",borderRadius:T.radiusSm,padding:14,fontSize:14,fontWeight:700,color:"#fff",cursor:"pointer",fontFamily:T.fontSans}}>Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
